import {useState, useCallback, useMemo, useEffect, useRef} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

export const useFilters = (initialFilters = {}, filterOptions = {}) => {
	const history = useHistory();
	const location = useLocation();
	const lastInitialFiltersRef = useRef(null);

	// Parse filter value from URL params or initial data
	const parseFilterValue = useCallback((value) => {
		if (!value) return [];
		if (Array.isArray(value)) return value;

		try {
			const parsed = JSON.parse(value);
			if (Array.isArray(parsed)) return parsed.map(String);
		} catch (e) {
			// Ignore parse errors
		}

		return value.split(',').filter(v => v.trim()).map(String);
	}, []);

	// Initialize state
	const [filters, setFilters] = useState({
		brands: [],
		categories: [],
		subcategories: [],
		countries: [],
		dietary: [],
		minPrice: 0,
		maxPrice: 1000,
		showOutOfStock: false,
		showPromotion: false,
		sort: "",
	});

	// Initialize from URL params or initial filters
	useEffect(() => {
		if (initialFilters && Object.keys(initialFilters).length > 0) {
			setFilters({
				brands: parseFilterValue(initialFilters.brand_id) || [],
				categories: parseFilterValue(initialFilters.cat_id) || [],
				subcategories: parseFilterValue(initialFilters.sub_cat_id) || [],
				countries: parseFilterValue(initialFilters.country_id) || [],
				dietary: parseFilterValue(initialFilters.diet_id) || [],
				minPrice: initialFilters.min || 0,
				maxPrice: initialFilters.max || 1000,
				showPromotion: initialFilters.promotion || false,
				sort: initialFilters.sort || "",
				showOutOfStock: initialFilters.oos || initialFilters.oos === 1 || initialFilters.oos === "1",
			});
		}
	}, [
		// Use stringified values to prevent infinite loop from array reference changes
		initialFilters?.brand_id?.join(','),
		initialFilters?.cat_id?.join(','),
		initialFilters?.sub_cat_id?.join(','),
		initialFilters?.country_id?.join(','),
		initialFilters?.diet_id?.join(','),
		initialFilters?.min,
		initialFilters?.max,
		initialFilters?.promotion,
		initialFilters?.oos,
		initialFilters?.sort,
		initialFilters?.q,
		parseFilterValue,
	]);

	// Toggle selection
	const toggleSelection = useCallback((type, item) => {
		setFilters(prev => {
			const currentIds = prev[type] || [];
			const itemValue = String(item.value);
			const isPresent = currentIds.some(id => String(id) === itemValue);

			return {
				...prev,
				[type]: isPresent
					? currentIds.filter(id => String(id) !== itemValue)
					: [...currentIds, itemValue]
			};
		});
	}, []);

	// Check if item is selected
	const isSelected = useCallback((type, value) => {
		return filters[type]?.some(id => String(id) === String(value));
	}, [filters]);

	// Update single filter
	const updateFilter = useCallback((key, value) => {
		setFilters(prev => ({...prev, [key]: value}));
	}, []);

	// Update multiple filters
	const updateFilters = useCallback((updates) => {
		setFilters(prev => ({...prev, ...updates}));
	}, []);

	// Clear all filters
	const clearFilters = useCallback(() => {
		setFilters({
			brands: [],
			categories: [],
			subcategories: [],
			countries: [],
			dietary: [],
			minPrice: 0,
			maxPrice: 1000,
			showOutOfStock: false,
			showPromotion: false,
			sort: "",
		});
		history.replace(location.pathname);
	}, [history, location.pathname]);

	// Count active filters
	const activeCount = useMemo(() => {
		let count = 0;
		['brands', 'categories', 'subcategories', 'countries', 'dietary'].forEach(key => {
			count += filters[key]?.length || 0;
		});
		if (filters.showPromotion) count++;
		if (filters.showOutOfStock) count++;
		if (filters.sort && filters.sort !== 'a-z') count++;
		if (filters.minPrice !== 0) count++;
		if (filters.maxPrice !== 1000) count++;
		return count;
	}, [filters]);

	return {
		filters,
		toggleSelection,
		isSelected,
		updateFilter,
		updateFilters,
		clearFilters,
		activeCount,
	};
};